import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { DualNotificationBells } from './NotificationBell';
import { usePermissions } from '@/contexts/PermissionsContext';
import { 
  Menu, X, Plus, LayoutDashboard, List, FileText, Settings, 
  BarChart2, Users, Truck, FileSpreadsheet, Upload, Database,
  Car, DollarSign, Shield, ChevronDown, ChevronUp, Mail, ClipboardList,
  IndianRupee, BookImage
} from 'lucide-react';

// Reports submenu items
const reportsNavItems = [
  { id: 'reports', label: 'Sales Reports', path: '/sales/reports', icon: FileText },
  { id: 'report-templates', label: 'Report Templates', path: '/sales/report-templates', icon: ClipboardList },
  { id: 'delivery', label: 'Delivery Report', path: '/sales/delivery-report', icon: Truck },
  { id: 'rto', label: 'RTO Report', path: '/sales/rto-report', icon: FileSpreadsheet },
  { id: 'financier', label: 'Financier Report', path: '/sales/financier-report', icon: DollarSign },
  { id: 'exchange', label: 'Exchange Vehicle Report', path: '/sales/exchange-vehicle-report', icon: Car },
  { id: 'insurance', label: 'Insurance Expiry', path: '/sales/reports/insurance-expiry', icon: Shield },
];

export function SalesLayout({ children, user, onLogout, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = usePermissions();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);

  const isActive = (path) => {
    if (path === '/sales/dashboard') return location.pathname === '/sales/dashboard';
    if (path === '/sales/bookings') {
      return location.pathname === '/sales/bookings' || 
             (location.pathname.startsWith('/sales/bookings/') && 
              !location.pathname.includes('/new') &&
              !location.pathname.includes('/edit'));
    }
    return location.pathname.startsWith(path);
  };

  const isReportsActive = () => {
    return reportsNavItems.some(item => location.pathname === item.path || location.pathname.startsWith(item.path));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false);
    setReportsOpen(false);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && !e.target.closest('.menu-panel') && !e.target.closest('.menu-toggle')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="min-h-screen page-wash-sales">
      <ImpersonationBanner />
      <Toaster position="top-right" richColors />

      {/* Header — premium frosted glass with indigo accent (Phase 5 Royal Twilight) */}
      <header className="sticky top-0 z-50 border-b border-indigo-100/70 backdrop-blur-xl bg-white/75 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button - visible on all screen sizes */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="menu-toggle p-3 hover:bg-indigo-50 rounded-xl transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 flex items-center justify-center min-w-[44px] min-h-[44px]"
                aria-label="Toggle menu"
                data-testid="sales-menu-toggle"
              >
                {menuOpen ? (
                  <X className="w-6 h-6 text-indigo-700 pointer-events-none" />
                ) : (
                  <Menu className="w-6 h-6 text-indigo-700 pointer-events-none" />
                )}
              </button>

              {/* Logo & Module */}
              <button 
                onClick={() => navigate('/modules')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_4px_12px_rgba(79,70,229,0.30)]" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 60%, #9333EA 100%)' }}>
                  <Car className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2 tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>
                    <span className="hidden sm:inline">Sales Module</span>
                    <span className="sm:hidden">Sales</span>
                  </h1>
                  <p className="text-[11px] text-slate-500 hidden sm:block tracking-wide font-medium mt-0.5">{title || 'AutoBook Pro'}</p>
                </div>
              </button>
            </div>

            {/* User Menu & Quick Add */}
            <div className="flex items-center gap-2">
              {/* Notification Bells */}
              <DualNotificationBells />
              {/* Quick Add Button */}
              <button
                onClick={() => navigate('/sales/bookings/new')}
                className="p-2 text-white rounded-full shadow-[0_8px_20px_rgba(79,70,229,0.35)] hover:shadow-[0_10px_28px_rgba(79,70,229,0.50)] transition-all duration-180 ease-out hover:scale-[1.05] active:scale-95 min-w-[40px] min-h-[40px] flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 60%, #9333EA 100%)' }}
                title="New Booking"
                data-testid="quick-add-booking"
              >
                <Plus className="w-5 h-5 pointer-events-none" />
              </button>
              
              <span className="text-sm text-slate-600 hidden lg:block font-medium">{user?.email}</span>
              <button
                onClick={onLogout}
                className="px-3 py-1.5 text-sm bg-rose-50 text-rose-700 rounded-xl border border-rose-100 hover:bg-rose-100 hover:border-rose-200 transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 hidden sm:block font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Slide-in Menu Panel - Same for Mobile and Desktop */}
      <div 
        className={`menu-panel fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Menu Header */}
        <div className="text-white p-5" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 50%, #9333EA 100%)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="w-6 h-6 pointer-events-none" />
              <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Geist, Inter, sans-serif' }}>Sales Module</span>
            </div>
            <button 
              onClick={() => setMenuOpen(false)}
              className="p-2 hover:bg-white/20 rounded-xl transition-all duration-180 ease-out min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="w-5 h-5 pointer-events-none" />
            </button>
          </div>
          <p className="text-indigo-100 text-[12.5px] mt-1.5 font-medium tracking-wide">{user?.email}</p>
        </div>

        {/* Navigation Items */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          <nav className="space-y-1">
            {/* 1. New Booking */}
            <button
              onClick={() => handleNavigation('/sales/bookings/new')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-240 ease-out ${
                location.pathname === '/sales/bookings/new'
                  ? 'text-white shadow-[0_8px_20px_rgba(79,70,229,0.30)]'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
              style={location.pathname === '/sales/bookings/new' ? { background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' } : {}}
              data-testid="menu-new-booking"
            >
              <div className={`p-2 rounded-lg ${location.pathname === '/sales/bookings/new' ? 'bg-white/20' : 'bg-indigo-100'}`}>
                <Plus className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold tracking-tight">New Booking</div>
                <div className={`text-[11px] ${location.pathname === '/sales/bookings/new' ? 'text-indigo-50' : 'text-indigo-600'} font-medium`}>Create new booking</div>
              </div>
            </button>

            {/* 2. Dashboard */}
            <button
              onClick={() => handleNavigation('/sales/dashboard')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                location.pathname === '/sales/dashboard'
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <LayoutDashboard className={`w-5 h-5 ${location.pathname === '/sales/dashboard' ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Dashboard</span>
              {location.pathname === '/sales/dashboard' && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 3. Sales Rep Performance */}
            <button
              onClick={() => handleNavigation('/sales/reports/sales-rep-performance')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/reports/sales-rep-performance')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Users className={`w-5 h-5 ${isActive('/sales/reports/sales-rep-performance') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Sales Rep Performance</span>
              {isActive('/sales/reports/sales-rep-performance') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 4. View All Bookings */}
            <button
              onClick={() => handleNavigation('/sales/bookings')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/bookings') && !location.pathname.includes('/new') && !location.pathname.includes('/edit')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <List className={`w-5 h-5 ${isActive('/sales/bookings') && !location.pathname.includes('/new') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>View All Bookings</span>
              {isActive('/sales/bookings') && !location.pathname.includes('/new') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 5. Reports (Collapsible) */}
            <div>
              <button
                onClick={() => setReportsOpen(!reportsOpen)}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                  isReportsActive()
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <BarChart2 className={`w-5 h-5 ${isReportsActive() ? 'text-indigo-600' : 'text-gray-500'}`} />
                  <span>Reports</span>
                </div>
                {reportsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {reportsOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-indigo-200 pl-4">
                  {reportsNavItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-sm ${
                          location.pathname === item.path
                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                            : 'text-gray-600 hover:bg-slate-100/80'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${location.pathname === item.path ? 'text-indigo-600' : 'text-gray-400'}`} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 6. MIS Reports */}
            <button
              onClick={() => handleNavigation('/sales/mis-reports')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/mis-reports')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileSpreadsheet className={`w-5 h-5 ${isActive('/sales/mis-reports') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>MIS Reports</span>
              {isActive('/sales/mis-reports') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 7. Multi Reports */}
            <button
              onClick={() => handleNavigation('/sales/multi-reports')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/multi-reports')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileText className={`w-5 h-5 ${isActive('/sales/multi-reports') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Multi Reports</span>
              {isActive('/sales/multi-reports') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 8. Stock */}
            <button
              onClick={() => handleNavigation('/sales/stock-report')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/stock-report')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Database className={`w-5 h-5 ${isActive('/sales/stock-report') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Stock</span>
              {isActive('/sales/stock-report') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 9. Masters */}
            <button
              onClick={() => handleNavigation('/sales/masters')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/masters')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings className={`w-5 h-5 ${isActive('/sales/masters') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Masters</span>
              {isActive('/sales/masters') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 10. Vehicle Price Master */}
            <button
              onClick={() => handleNavigation('/sales/vehicle-price-master')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/vehicle-price-master')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="menu-vehicle-price-master"
            >
              <IndianRupee className={`w-5 h-5 ${isActive('/sales/vehicle-price-master') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Vehicle Price Master</span>
              {isActive('/sales/vehicle-price-master') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 10b. Catalog Master */}
            <button
              onClick={() => handleNavigation('/sales/catalog-master')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/catalog-master')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="menu-catalog-master"
            >
              <BookImage className={`w-5 h-5 ${isActive('/sales/catalog-master') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Catalog Master</span>
              {isActive('/sales/catalog-master') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 11. Templates (Email & WhatsApp) */}
            <button
              onClick={() => handleNavigation('/sales/templates')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/templates')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="menu-templates"
            >
              <Mail className={`w-5 h-5 ${isActive('/sales/templates') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Templates</span>
              {isActive('/sales/templates') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 11. Bulk Upload */}
            <button
              onClick={() => handleNavigation('/sales/bulk-upload')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/bulk-upload')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Upload className={`w-5 h-5 ${isActive('/sales/bulk-upload') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Bulk Upload</span>
              {isActive('/sales/bulk-upload') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 11. Google Sheets */}
            <button
              onClick={() => handleNavigation('/sales/google-sheets')}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive('/sales/google-sheets')
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileSpreadsheet className={`w-5 h-5 ${isActive('/sales/google-sheets') ? 'text-indigo-600' : 'text-gray-500'}`} />
              <span>Google Sheets</span>
              {isActive('/sales/google-sheets') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
            </button>

            {/* 12. Database Backup (Super Admin only) */}
            {isSuperAdmin && (
              <button
                onClick={() => handleNavigation('/sales/database-backup')}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  isActive('/sales/database-backup')
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Database className={`w-5 h-5 ${isActive('/sales/database-backup') ? 'text-indigo-600' : 'text-gray-500'}`} />
                <span>Database Backup</span>
                {isActive('/sales/database-backup') && <div className="ml-auto w-2 h-2 bg-indigo-600 rounded-full" />}
              </button>
            )}
          </nav>

          {/* Footer Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                navigate('/modules');
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 p-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              <span>Back to Modules</span>
            </button>
            
            <button
              onClick={() => {
                onLogout();
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 p-3 text-red-600 hover:bg-red-50 rounded-xl transition-all mt-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Bottom Add Button (Mobile) */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
          <button
            onClick={() => handleNavigation('/sales/bookings/new')}
            className="w-full py-3 text-white font-medium rounded-xl shadow-[0_8px_24px_rgba(79,70,229,0.35)] hover:shadow-[0_12px_32px_rgba(79,70,229,0.50)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 60%, #9333EA 100%)' }}
            data-testid="bottom-add-booking-btn"
          >
            <Plus className="w-5 h-5" />
            <span>New Booking</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 anim-fade-in">
        <div className="anim-slide-up">{children}</div>
      </main>
    </div>
  );
}

export default SalesLayout;
